import { IArticleStock } from '@boss/types/b2b-b2c';

import {
  StockAvailability,
  StockStatus,
  StockStatusLabelTranslations,
  StockStatusLine,
  StockStatusSummary,
  StockWarehouseInput,
} from './types';

const DELIVERY_WAREHOUSE = 'HM';

/**
 * Maps an API availability to a normalized status and the label supplied by the consumer's translation module.
 * Missing or unrecognized availability values fall back to the no-stock label (defensive: no stock data → no stock label).
 */
export const getStockStatusLabel = (
  availability: StockAvailability | null | undefined,
  translations: StockStatusLabelTranslations,
): { status: StockStatus; label: string } => {
  switch (availability) {
    case 'High Stock':
      return { status: 'available', label: translations.available };
    case 'Low Stock':
      return { status: 'insufficient', label: translations.insufficient };
    default:
      return { status: 'unavailable', label: translations.unavailable };
  }
};

/**
 * Maps the stock API response to one line per requested skuid.
 * `null` response (HTTP 204) or a missing line for a requested skuid → unavailable + no-stock label, warehouse `null`.
 */
export const applyStockResponse = (
  requestedSkuids: string[],
  response: IArticleStock | null,
  translations: StockStatusLabelTranslations,
): StockStatusLine[] => {
  const stockLines = response ?? [];

  return requestedSkuids.map(skuid => {
    const stockLine = stockLines.find(line => line.id === skuid);
    const { status, label } = getStockStatusLabel(stockLine?.availability, translations);

    return {
      skuid,
      warehouse: stockLine?.warehouse ?? null,
      status,
      label,
    };
  });
};

/**
 * Any low/out-of-stock line → red; all lines in stock → green; no-status lines → null (empty indicator).
 */
export const summarizeStockStatus = (lines: StockStatusLine[]): StockStatusSummary => {
  if (lines.some(({ status }) => status === 'insufficient' || status === 'unavailable')) {
    return 'red';
  }

  if (lines.length > 0 && lines.every(({ status }) => status === 'available')) {
    return 'green';
  }

  return null;
};

/**
 * Delivery → the main warehouse; pickup → the chosen shop's warehouse; no shop chosen → no stock check possible.
 */
export const resolveStockWarehouses = ({ deliveryMethod, shopWarehouseCode }: StockWarehouseInput): string[] => {
  if (deliveryMethod === 'Pickup') {
    return shopWarehouseCode ? [shopWarehouseCode] : [];
  }

  return [DELIVERY_WAREHOUSE];
};
